// plan-06 S02/S03 + plan-10 S03: consensus directory widget — bible §25
// Refactored: implements StageWidget interface; directory command toggles stage

import { DIRECTORY, type DirectoryNode } from '../data/directory'
import { play } from '../audio/sfx'
import { createStreamTarget } from '../oracle/streaming'
import { onLongPress } from '../core/touch'
import type { CommandCtx } from '../oracle/commands'
import type { StageWidget, StageHandle, StageCtx } from './stage/types'

const CA_NODE = DIRECTORY.find((n) => n.kind === 'ca')
const CA_FULL = CA_NODE?.url ?? ''

const DISPLAY_LABELS: Record<string, string> = {
  'CA': 'contract address',
  'website (main)': 'vaguepostking.com',
  'website ("vague souls" game)': 'vague souls',
}

function shortUrl(node: DirectoryNode): string {
  if (node.kind === 'ca') {
    return node.url.slice(0, 4) + ' ' + node.url.slice(4, 8) + ' ... pump'
  }
  try {
    const u = new URL(node.url)
    const path = u.pathname.replace(/\/$/, '')
    const short = path.length > 24 ? path.slice(0, 21) + '...' : path
    return u.hostname + short
  } catch {
    return node.url
  }
}

function directoryNodes(): DirectoryNode[] {
  return DIRECTORY.filter((n) => {
    if (n.kind === 'ca') return true
    if (n.kind === 'self' || n.kind === 'portal') return true
    return n.url.startsWith('http')
  })
}

let leakSeenThisSession = false
let type3ComboFired = false

// --- StageWidget implementation ---

export const directoryWidget: StageWidget = {
  id: 'directory',
  label: 'CONSENSUS DIRECTORY // OBSERVED NODES',
  mount(host: HTMLElement, ctx: StageCtx): StageHandle {
    const nodes = directoryNodes()

    const list = document.createElement('div')
    list.className = 'vt-directory-list'

    for (const node of nodes) {
      const row = document.createElement('div')
      row.className = 'vt-directory-row'
      row.setAttribute('data-kind', node.kind)
      row.setAttribute('data-label', node.label)

      const num = String(node.id).padStart(2, '0')
      const displayLabel = DISPLAY_LABELS[node.label] ?? node.label

      const left = document.createElement('span')
      left.className = 'vt-directory-row-left'
      left.textContent = `node ${num}  ◇ ${displayLabel}`
      row.appendChild(left)

      const right = document.createElement('span')
      right.className = 'vt-directory-row-right'

      if (node.kind === 'ca') {
        const urlText = document.createElement('span')
        urlText.className = 'vt-directory-url'
        urlText.textContent = shortUrl(node)
        right.appendChild(urlText)

        const btn = document.createElement('button')
        btn.className = 'vt-directory-btn'
        btn.textContent = '[ copy ]'
        btn.addEventListener('click', (e) => {
          e.stopPropagation()
          copyCA(row)
        })
        right.appendChild(btn)
      } else if (node.kind === 'self') {
        const here = document.createElement('span')
        here.className = 'vt-directory-self'
        here.textContent = '[ you are here ]'
        right.appendChild(here)
      } else if (node.kind === 'portal') {
        const urlText = document.createElement('span')
        urlText.className = 'vt-directory-url'
        urlText.textContent = shortUrl(node)
        right.appendChild(urlText)

        const btn = document.createElement('button')
        btn.className = 'vt-directory-btn'
        btn.textContent = '[ engage ]'
        btn.addEventListener('click', (e) => {
          e.stopPropagation()
          ctx.emitOracle('> portal opening. proximity: probably.')
          // import and trigger portal engage
          import('./portal').then(({ triggerEngage }) => {
            triggerEngage()
          }).catch(() => {})
        })
        right.appendChild(btn)
      } else {
        const urlText = document.createElement('span')
        urlText.className = 'vt-directory-url'
        urlText.textContent = shortUrl(node)
        right.appendChild(urlText)

        const arrow = document.createElement('span')
        arrow.className = 'vt-directory-arrow'
        arrow.textContent = ' ↗'
        right.appendChild(arrow)

        row.addEventListener('click', () => {
          window.open(node.url, '_blank', 'noopener,noreferrer')
        })
      }

      row.appendChild(right)

      // palimpsest leak on twitter row hover (desktop) or long-press (touch)
      if (node.label === 'twitter') {
        const showLeak = () => {
          if (row.querySelector('.vt-directory-leak')) return

          const leak = document.createElement('div')
          leak.className = 'vt-directory-leak'
          leak.textContent = '> the handle is alive. the king is not.'

          if (leakSeenThisSession) {
            leak.style.opacity = '0.5'
          }

          row.appendChild(leak)
          leakSeenThisSession = true

          if (!type3ComboFired) {
            type3ComboFired = true
            import('../eggs/palimpsest').then(({ triggerType3Combo }) => {
              triggerType3Combo()
            }).catch(() => {})
          }
        }

        row.addEventListener('mouseenter', showLeak)

        row.addEventListener('mouseleave', () => {
          const leak = row.querySelector('.vt-directory-leak')
          if (leak) {
            const el = leak as HTMLElement
            el.style.opacity = '0.5'
          }
        })

        // plan-11 S07: long-press for touch devices (replaces hover)
        onLongPress(row, () => showLeak())
      }

      list.appendChild(row)
    }

    host.appendChild(list)

    const quorum = document.createElement('div')
    quorum.className = 'vt-directory-quorum'
    quorum.textContent = `@quorum: ${nodes.length} / ${nodes.length} nodes online. probably.`
    host.appendChild(quorum)

    return {
      dismiss() {
        // cleanup: nothing persistent to cancel
      },
    }
  },
}

function copyCA(row: HTMLElement): void {
  navigator.clipboard.writeText(CA_FULL).then(() => {
    play('command_accept')
    row.classList.add('vt-directory-flash')
    setTimeout(() => row.classList.remove('vt-directory-flash'), 1600)

    const history = document.querySelector('.vt-history') as HTMLElement | null
    if (history) {
      const log = createStreamTarget(history, 'vt-line-oracle')
      log.style.color = 'var(--vt-green-dim)'
      log.textContent = '> ca copied. probably useful.'
      // trigger anchor scroll
      const anchor = (window as unknown as Record<string, unknown>).__vtScheduleAnchor
      if (typeof anchor === 'function') (anchor as () => void)()
    }
  }).catch(() => {})
}

// Oracle command: --text mode (legacy inline dump)
export async function handleDirectoryText(ctx: CommandCtx): Promise<void> {
  const nodes = directoryNodes()
  const target = createStreamTarget(ctx.history, 'vt-line-oracle')

  let text = '@directory\n'
  for (const node of nodes) {
    const num = String(node.id).padStart(2, '0')
    const displayLabel = DISPLAY_LABELS[node.label] ?? node.label
    let action = ''

    if (node.kind === 'ca') {
      action = shortUrl(node) + '  [ copy ]'
    } else if (node.kind === 'self') {
      action = '[ you are here ]'
    } else if (node.kind === 'portal') {
      action = shortUrl(node) + '  [ engage ]'
    } else {
      action = shortUrl(node) + ' ↗'
    }

    text += `node ${num}  ◇ ${displayLabel.padEnd(22)}${action}\n`
  }
  text += `@quorum: ${nodes.length} / ${nodes.length} nodes online. probably.`

  ctx.cursor.attach(target)
  await ctx.streamFn(target, text, { signal: ctx.abortSignal })
  ctx.cursor.detach()
}
